import { tickAltarCapture, TT_ALTAR_CONFIG } from './map/ttAltarRules.js';
import { ARAM_RELIC_PADS } from './map/aramRelicPads.js';
import { ARAM_BRIDGE_WIDTH, ARAM_HALF_LENGTH } from './map/aramScale.js';
import { ARAM_STRUCTURE_ALONG } from './map/aramLayout.js';
import { ARAM_BOUND_PAD } from './map/aramBridgeCollision.js';
import { MP_TEST_MAP_ID } from './map/mpTestDefaults.js';
import { ARAM_RELIC_HEAL_RATIO } from './map/aramHealConstants.js';

const ARAM_SPAWN_Z = ARAM_STRUCTURE_ALONG.spawn;
const ARAM_OUTER_Z = ARAM_STRUCTURE_ALONG.outer;

export const MAP_CONFIGS = {
  test_map: {
    id: 'test_map',
    name: 'Test & Debug Sandbox Map',
    isTestMap: true,
    width: 32000,
    height: 32000,
    backgroundUrl: 'https://wiki.leagueoflegends.com/en-us/images/Proving_Grounds_map.png',
    passiveGoldRate: 10.0, // Increased gold rate for fast debugging
    passiveXpRate: 0,
    bounds: { minX: -30, maxX: 30, minZ: -30, maxZ: 30 },
    lanes: [
      { id: 'mid', points: [{ x: -22, z: -22 }, { x: 0, z: 0 }, { x: 22, z: 22 }] }
    ],
    jungleCamps: [
      { id: 'blue_golem', x: -12, z: 8 },
      { id: 'red_lizard', x: 12, z: -8 },
      { id: 'dragon', x: -16, z: 12 },
      { id: 'baron', x: 16, z: -12 }
    ],
    relics: [
      { id: 'relic-1', x: 9000, y: 10000, respawnTimer: 10, active: true },
      { id: 'relic-2', x: 15000, y: 6000, respawnTimer: 10, active: true }
    ],
    turrets: [
      { id: 'blue-t1', team: 'blue', x: -18, z: -18, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 },
      { id: 'red-t1', team: 'red', x: 18, z: 18, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 }
    ]
  },
  aram_proving_grounds: {
    id: 'aram_proving_grounds',
    name: 'The Proving Grounds (ARAM)',
    width: 24000,
    height: 16000,
    backgroundUrl: 'https://wiki.leagueoflegends.com/en-us/images/Proving_Grounds_map.png',
    passiveGoldRate: 2.5,
    /** AR-R40: accelerated passive XP (XP/s) — Proving Grounds video analysis. */
    passiveXpRate: 6,
    bounds: {
      minX: -(ARAM_BRIDGE_WIDTH / 2 + ARAM_BOUND_PAD),
      maxX: ARAM_BRIDGE_WIDTH / 2 + ARAM_BOUND_PAD,
      minZ: -(ARAM_HALF_LENGTH + ARAM_BOUND_PAD),
      maxZ: ARAM_HALF_LENGTH + ARAM_BOUND_PAD
    },
    lanes: [
      { id: 'mid', points: [{ x: 0, z: ARAM_SPAWN_Z }, { x: 0, z: 0 }, { x: 0, z: -ARAM_SPAWN_Z }] }
    ],
    jungleCamps: [],
    relics: ARAM_RELIC_PADS.map(pad => ({
      id: pad.id,
      x: pad.x,
      z: pad.z,
      cooldownTimer: 0,
      active: true
    })),
    turrets: [
      {
        id: 'blue-outer', team: 'blue', x: 0, z: ARAM_OUTER_Z,
        hp: 2550, maxHp: 2550, ad: 152, attackRange: 775
      },
      {
        id: 'red-outer', team: 'red', x: 0, z: -ARAM_OUTER_Z,
        hp: 2550, maxHp: 2550, ad: 152, attackRange: 775
      }
    ]
  },
  twisted_treeline_3v3: {
    id: 'twisted_treeline_3v3',
    name: 'Twisted Treeline (3v3)',
    width: 24000,
    height: 18000,
    backgroundUrl: 'https://wiki.leagueoflegends.com/en-us/images/Twisted_Treeline_map.png',
    passiveGoldRate: 1.9,
    passiveXpRate: 0,
    bounds: { minX: -16, maxX: 16, minZ: -12, maxZ: 12 },
    lanes: [
      { id: 'top', points: [{ x: -12, z: -8 }, { x: 0, z: -8 }, { x: 12, z: -8 }] },
      { id: 'bot', points: [{ x: -12, z: 8 }, { x: 0, z: 8 }, { x: 12, z: 8 }] }
    ],
    jungleCamps: [
      { id: 'wraiths', x: 0, z: -2 },
      { id: 'wolves', x: 0, z: 2 },
      { id: 'vilemaw', x: 0, z: -10 }
    ],
    altars: [
      { id: 'west-altar', x: -7, z: 0, capturedBy: null, captureProgress: 0 },
      { id: 'east-altar', x: 7, z: 0, capturedBy: null, captureProgress: 0 }
    ],
    turrets: [
      { id: 'blue-top-outer', team: 'blue', x: -8, z: -8, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 },
      { id: 'red-top-outer', team: 'red', x: 8, z: -8, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 },
      { id: 'blue-bot-outer', team: 'blue', x: -8, z: 8, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 },
      { id: 'red-bot-outer', team: 'red', x: 8, z: 8, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 }
    ]
  },
  summoners_rift_5v5: {
    id: 'summoners_rift_5v5',
    name: "Summoner's Rift (5v5)",
    status: 'pending',
    width: 30000,
    height: 30000,
    backgroundUrl: 'https://wiki.leagueoflegends.com/en-us/images/Summoner%27s_Rift_map.png',
    passiveGoldRate: 1.3,
    passiveXpRate: 0,
    bounds: { minX: -20, maxX: 20, minZ: -20, maxZ: 20 },
    lanes: [
      { id: 'top', points: [{ x: -16, z: -16 }, { x: -16, z: 16 }, { x: 16, z: 16 }] },
      { id: 'mid', points: [{ x: -16, z: -16 }, { x: 0, z: 0 }, { x: 16, z: 16 }] },
      { id: 'bot', points: [{ x: -16, z: -16 }, { x: 16, z: -16 }, { x: 16, z: 16 }] }
    ],
    jungleCamps: [
      { id: 'blue_golem', x: -8, z: 4 },
      { id: 'red_lizard', x: 8, z: -4 },
      { id: 'wolves', x: -6, z: -2 },
      { id: 'wraiths', x: 6, z: 2 },
      { id: 'small_golems', x: 10, z: -8 },
      { id: 'dragon', x: -9, z: 9 },
      { id: 'baron', x: 9, z: -9 }
    ],
    turrets: [
      { id: 'blue-mid-t1', team: 'blue', x: -10, z: -10, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 },
      { id: 'red-mid-t1', team: 'red', x: 10, z: 10, hp: 2550, maxHp: 2550, ad: 152, attackRange: 775 }
    ]
  }
};

export const MAPS = MAP_CONFIGS;

export function getMapConfig(mapId) {
  return MAP_CONFIGS[mapId] || MAP_CONFIGS[MP_TEST_MAP_ID];
}

/** @deprecated Prefer tickAltarCapture from map/ttAltarRules.js */
export function captureAltar(altar, team, dtSeconds) {
  const teamId = typeof team === 'string' ? team : team?.team;
  return tickAltarCapture(altar, teamId, dtSeconds, {
    gameTime: TT_ALTAR_CONFIG.unlockTime,
    onAltar: true
  });
}

export function consumeHealthRelic(relic, champion, currentTime = 0) {
  const isAvail = relic.isAvailable !== undefined ? relic.isAvailable : relic.active;
  if (!isAvail) {
    return { consumed: false, relic, player: champion };
  }

  const dist = Math.hypot(champion.x - relic.x, champion.y - relic.y);
  if (dist <= 400) {
    const hpRestore = champion.maxHp * ARAM_RELIC_HEAL_RATIO;
    const mpRestore = (champion.maxMp || 0) * ARAM_RELIC_HEAL_RATIO;

    const updatedPlayer = {
      ...champion,
      hp: Math.min(champion.maxHp, champion.hp + hpRestore),
      mp: champion.maxMp ? Math.min(champion.maxMp, (champion.mp || 0) + mpRestore) : champion.mp
    };

    const updatedRelic = {
      ...relic,
      isAvailable: false,
      active: false,
      lastConsumedTime: currentTime
    };

    return {
      consumed: true,
      player: updatedPlayer,
      champion: updatedPlayer,
      relic: updatedRelic
    };
  }

  return { consumed: false, relic, player: champion, champion };
}
